/**
 * Safe localStorage seam for the public static Typewriter site.
 *
 * The site must stay usable when storage is absent, blocked (privacy modes,
 * disabled cookies/site data), or corrupt. Every read/write goes through this
 * module: failures degrade to "no persistence" instead of breaking the app,
 * and corrupt JSON is discarded rather than thrown.
 */

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function getStorage(): StorageLike | null {
  if (typeof window === 'undefined') return null;
  try {
    const storage = window.localStorage;
    // Touch the storage: some browsers only throw on access, not on lookup.
    const probe = storage.getItem('__typewriter_storage_probe__');
    return probe === null ? storage : storage;
  } catch {
    return null;
  }
}

export function storageAvailable(): boolean {
  return getStorage() !== null;
}

export function readStoredString(key: string): string | null {
  try {
    return getStorage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function writeStoredString(key: string, value: string): boolean {
  const storage = getStorage();
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function removeStoredKey(key: string): void {
  try {
    getStorage()?.removeItem(key);
  } catch {
    // Blocked storage has nothing to remove.
  }
}

/**
 * Read and parse a JSON value. Corrupt JSON is removed and the fallback
 * returned so one bad write cannot permanently break a feature.
 */
export function readStoredJson<T>(key: string, fallback: T): T {
  const raw = readStoredString(key);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    removeStoredKey(key);
    return fallback;
  }
}

/** Serialize and store a JSON value; returns false when storage is blocked. */
export function writeStoredJson(key: string, value: unknown): boolean {
  try {
    return writeStoredString(key, JSON.stringify(value));
  } catch {
    return false;
  }
}

/** Remove every Typewriter-owned key (device-local progress reset). */
export function clearTypewriterStorage(keys: readonly string[]): void {
  for (const key of keys) {
    removeStoredKey(key);
  }
}
